import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Leaf, Factory, Palette } from 'lucide-react';

const Home = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div 
        className="relative h-[600px] bg-cover bg-center flex items-center justify-center"
        style={{ 
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(https://images.pexels.com/photos/13786211/pexels-photo-13786211.jpeg)',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="hero-title">
            {t.home.hero.title}
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto" data-testid="hero-subtitle">
            {t.home.hero.subtitle}
          </p>
          <Link 
            to="/activities" 
            className="inline-flex items-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            data-testid="hero-cta-button"
          >
            <span>{t.home.hero.cta}</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </div>

      {/* Introduction Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-stone-800 mb-4">{t.home.intro.title}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.home.intro.description}
          </p>
        </div>

        {/* Three Poles */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Pole Agro */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-testid="pole-agro-card">
            <div className="h-48 bg-cover bg-center" style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/5kqww6he_IMG-20260221-WA0006.jpg)' }}></div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Leaf className="text-green-600" size={32} />
                <h3 className="text-2xl font-bold text-stone-800">Pôle Agro-Alimentaire</h3>
              </div>
              <p className="text-gray-600 mb-4">
                De la culture à la distribution, nous contrôlons chaque étape pour garantir une qualité irréprochable.
              </p>
              <Link to="/activities" className="text-amber-600 hover:text-amber-700 font-semibold flex items-center space-x-1">
                <span>En savoir plus</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Pole Industry */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-testid="pole-industry-card">
            <div className="h-48 bg-cover bg-center" style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/km009rd4_IMG-20260221-WA0005.jpg)' }}></div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Factory className="text-orange-600" size={32} />
                <h3 className="text-2xl font-bold text-stone-800">Pôle Industrie</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Depuis 1993, partenaire de confiance pour le tissu industriel tunisien avec des solutions techniques de pointe.
              </p>
              <Link to="/activities" className="text-amber-600 hover:text-amber-700 font-semibold flex items-center space-x-1">
                <span>En savoir plus</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Pole Art */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-testid="pole-art-card">
            <div className="h-48 bg-cover bg-center" style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-generator-110/artifacts/lic9eacp_IMG-20260221-WA0004.jpg)' }}></div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-3">
                <Palette className="text-purple-600" size={32} />
                <h3 className="text-2xl font-bold text-stone-800">Pôle Art & Design</h3>
              </div>
              <p className="text-gray-600 mb-4">
                L'alliance de l'esthétique et de l'innovation avec nos mosaïques décoratives en verre.
              </p>
              <Link to="/activities" className="text-amber-600 hover:text-amber-700 font-semibold flex items-center space-x-1">
                <span>En savoir plus</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-stone-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Prêts à collaborer ?</h2>
          <p className="text-xl mb-8 text-gray-300">Découvrez comment Barkatis Group peut répondre à vos besoins</p>
          <Link 
            to="/contact" 
            className="inline-block bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            data-testid="cta-contact-button"
          >
            Contactez-nous
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;